import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Descriptions, Empty, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { PgProviderFeeHistory } from '../../../../lib/api/pgProviderFee';
import { SettingsSection } from '../../../pgProviderRoute/common/SettingsSection';
import { SettingsModal } from '../../../pgProviderRoute/common/SettingsModal';
import { formatLogDate } from '../../../pgProviderRoute/utils/formatLogDate';
import { pgProviderFeeQuery } from '../../queries';
import { FeeHistoryStyle, HistorySnapshotStyle } from './styles';

export const FEE_HISTORY_LIMIT = 50;

export const FeeHistoryPanel = (): React.JSX.Element => {
  const query = useQuery({ ...pgProviderFeeQuery.histories(FEE_HISTORY_LIMIT), refetchOnMount: 'always' });
  const [selected, setSelected] = useState<PgProviderFeeHistory | null>(null);

  const adminLabel = (history: PgProviderFeeHistory): string =>
    history.adminName ?? (history.adminId ? '관리자 #' + history.adminId : '시스템');

  const columns: ColumnsType<PgProviderFeeHistory> = [
    { title: '변경 일시', dataIndex: 'createDate', width: 170, render: (date: string) => formatLogDate(date) },
    { title: '변경자', dataIndex: 'adminName', width: 120, render: (_, record) => adminLabel(record) },
    {
      title: '변경 내용',
      dataIndex: 'changes',
      render: (changes: string[], record) => (
        <div className="history-summary">
          {changes.slice(0, 2).map((change, index) => (
            <div key={index}>{change}</div>
          ))}
          {changes.length > 2 && <span className="settings-muted">외 {changes.length - 2}개 변경</span>}
          {record.reason && <div className="history-reason">사유: {record.reason}</div>}
        </div>
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, record) => (
        <Button type="link" onClick={() => setSelected(record)}>
          상세
        </Button>
      ),
    },
  ];

  return (
    <FeeHistoryStyle>
      <div className="settings-panel-header">
        <div>
          <h2>수수료 변경 이력</h2>
          <p>
            최신 {FEE_HISTORY_LIMIT}건을 조회합니다. 저장 직후에는 이력 반영까지 잠시 걸릴 수 있습니다.
          </p>
        </div>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
          새로고침
        </Button>
      </div>
      {query.isError ? (
        <Alert type="error" showIcon message="변경 이력을 불러오지 못했습니다. 새로고침해 주세요." />
      ) : (
        <SettingsSection title="변경 기록">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={query.data?.histories ?? []}
            loading={query.isLoading}
            pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
            scroll={{ x: 680 }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 변경 이력이 없습니다." />,
            }}
          />
        </SettingsSection>
      )}
      <SettingsModal
        title="수수료 변경 상세"
        open={selected !== null}
        onCancel={() => setSelected(null)}
        footer={<Button onClick={() => setSelected(null)}>닫기</Button>}
        width={760}
      >
        {selected && (
          <>
            <Descriptions
              column={2}
              size="small"
              items={[
                { key: 'admin', label: '변경자', children: adminLabel(selected) },
                { key: 'date', label: '변경 일시', children: formatLogDate(selected.createDate, true) },
                { key: 'reason', label: '변경 사유', children: selected.reason ?? '없음', span: 2 },
              ]}
            />
            <ul>
              {selected.changes.map((change, index) => (
                <li key={index}>{change}</li>
              ))}
            </ul>
            <details>
              <summary>변경 후 전체 수수료 보기</summary>
              <HistorySnapshotStyle>{JSON.stringify(selected.snapshot, null, 2)}</HistorySnapshotStyle>
            </details>
          </>
        )}
      </SettingsModal>
    </FeeHistoryStyle>
  );
};
